import {
  CollapseDS,
  SidePanelDS,
  Spacer,
  TextAreaDS,
  TextCapitalized,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { formatMileage } from "../../../../../libSlice/materialUiFormats";
import type { VehicleSession, VehicleSessionRestitution } from "../../fleetApi";
import { CleaningTag } from "../cleaningTag";
import "../../../../../i18n/i18n";

export const RestitutionVehicleState = (props: {
  sessionWithFinalRestitution: VehicleSession;
  collapsedInitialRestitution?: VehicleSessionRestitution;
}) => {
  const { sessionWithFinalRestitution, collapsedInitialRestitution } = props;
  const { t } = useTranslation();

  return (
    <SidePanelDS.Section verticalSpacing={2} horizontalSpacing={1.5}>
      <SectionTitle>
        <TextCapitalized>
          {t("fleet.vehicleSessions.sidepanel.state.title")}
        </TextCapitalized>
      </SectionTitle>
      <Spacer y={2} />
      <RestitutionState
        mileage={sessionWithFinalRestitution.mileage}
        isCabinCleaned={sessionWithFinalRestitution.isCabinCleaned}
        isTrunkCleaned={sessionWithFinalRestitution.isTrunkCleaned}
        observations={sessionWithFinalRestitution.observations}
      />
      {collapsedInitialRestitution && (
        <>
          <Spacer y={2} />
          <CollapseDS
            label={t("fleet.vehicleSessions.sidepanel.state.seeInitial")}
          >
            <Spacer y={1} />
            <RestitutionState
              mileage={collapsedInitialRestitution.mileage}
              isCabinCleaned={collapsedInitialRestitution.isCabinCleaned}
              isTrunkCleaned={collapsedInitialRestitution.isTrunkCleaned}
              observations={collapsedInitialRestitution.observations}
            />
          </CollapseDS>
        </>
      )}
    </SidePanelDS.Section>
  );
};

const RestitutionState = (props: {
  mileage: number | null;
  isCabinCleaned: boolean | null;
  isTrunkCleaned: boolean | null;
  observations: string | null;
}) => {
  const { mileage, isCabinCleaned, isTrunkCleaned, observations } = props;
  const { t } = useTranslation();

  return (
    <>
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.state.mileage")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          <StyledValue>
            <TextCapitalized>
              {mileage !== null ? formatMileage(mileage) : "-"}
            </TextCapitalized>
          </StyledValue>
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.state.isCabinCleaned")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          isCabinCleaned !== null ? (
            <CleaningTag isCleaned={isCabinCleaned} />
          ) : (
            <StyledValue>-</StyledValue>
          )
        }
      />
      <Spacer y={2} />
      <SidePanelDS.SectionRow
        label={
          <SectionRowLabel>
            <TextCapitalized>
              {t("fleet.vehicleSessions.sidepanel.state.isTrunkCleaned")}
            </TextCapitalized>
          </SectionRowLabel>
        }
        value={
          isTrunkCleaned !== null ? (
            <CleaningTag isCleaned={isTrunkCleaned} />
          ) : (
            <StyledValue>-</StyledValue>
          )
        }
      />
      <Spacer y={2} />
      <SectionTitle>
        <TextCapitalized>
          {t("fleet.vehicleSessions.sidepanel.state.observations")}
        </TextCapitalized>
      </SectionTitle>
      <Spacer y={0.5} />
      <TextAreaDS
        value={observations ?? ""}
        $update={() => {}}
        isDisabled={true}
      />
    </>
  );
};

const SectionTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/darkGrey"]}
`;

const SectionRowLabel = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;

const StyledValue = styled.div`
  text-align: right;
`;
